import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import styles from "./AuthPage.module.css";
import LoginBroadcast from "../LoginBroadcast";
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SchoolIcon from '@mui/icons-material/School';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import { ENV_CONFIG, FEATURE_FLAGS, EDUGATE_API_URL, VISITOR_AUTH_URL, GVA_FRONTEND_URL, HEP_AUTHING_APP_ID, HEP_AUTHING_ORIGIN, HEP_AUTHING_REDIRECT_URI } from '../../config/serviceConfig';
import { clearPendingLocalLogin, getPendingLocalLogin } from '../../utils/localLogin';
import { SUPPORTED_LANGUAGES, switchLanguage, getCurrentLanguageLabel, getPreferredLanguage } from '../../config/languages';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';

// ==================== 配置变量区域 ====================
// 🔧 环境配置开关：使用统一配置
const IS_DEVELOPMENT = !ENV_CONFIG.IS_PRODUCTION;

// 🔧 北邮CAS服务器配置
const CAS_SERVER_URL = IS_DEVELOPMENT 
  ? "http://localhost:6066"                    // 开发环境：模拟CAS服务器
  : "https://auth.bupt.edu.cn";                // 生产环境：北邮CAS服务器

// 🔧 北邮CAS回调地址配置（使用统一配置）
const CAS_CALLBACK_URL = `${EDUGATE_API_URL}/api/cas-callback`;

// 🆕 教育智联网CAS服务器配置// 生产环境通过 nginx /edunet-sso/ 反代，避免 HTTPS 页面直跳 HTTP 触发 Mixed Content 拦截
const EDUNET_CAS_SERVER_URL = IS_DEVELOPMENT 
  ? "http://localhost:6066"                    // 开发环境：模拟CAS服务器
  : `${window.location.protocol}//${window.location.host}/edunet-sso`; // 生产环境：同域 nginx 反代

// 🆕 教育智联网CAS回调地址配置
const EDUNET_CAS_CALLBACK_URL = `${EDUGATE_API_URL}/api/edunet-cas-callback`;

// 访客认证服务URL（使用统一配置）
const AUTH_SERVER_URL = VISITOR_AUTH_URL;
const REMOTE_SMS_SERVER_URL = VISITOR_AUTH_URL;

const Spinner = () => <div className={styles.spinner}></div>;

// 协议声明栏
function LegalBar() {
  const docs = [
    { label: '用户协议',    file: '用户协议.md' },
    { label: '隐私政策',    file: '隐私政策.md' },
    { label: '账号服务须知', file: '账号服务须知.md' },
  ];
  return (
    <div className={styles.legalBar}>
      <div className={styles.legalBarLine1}>
        登录并继续使用，视为您已阅读并同意&nbsp;
        {docs.map((doc, i) => (
          <span key={doc.file}>
                        <a className={styles.legalLink} href={`/legal/viewer?file=${encodeURIComponent(doc.file)}`} target="_blank" rel="noopener noreferrer">
              《{doc.label}》
            </a>
            {i < docs.length - 1 ? '\u00a0' : ''}
          </span>
        ))}
      </div>
      <div className={styles.legalBarLine2}>
        平台内容由 AI 生成，仅用于教学训练参考
      </div>
    </div>
  );
}

function LoginForm({ onLogin, onSwitchToRegister }) {
  const { t } = useTranslation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginType, setLoginType] = useState("school"); // 'school' | 'guest'
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSchoolLogin = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      const apiUrl = `${EDUGATE_API_URL}/api/login`;

      const localRequestID = getPendingLocalLogin();
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username,
          password,
          ...(localRequestID ? { local_request_id: localRequestID } : {}),
        }),
      });
      const data = await response.json();

      if (response.ok) {
        if (data.data?.local_redirect_url) {
          clearPendingLocalLogin();
          window.location.replace(data.data.local_redirect_url);
          return;
        }
        if (localRequestID && data.data?.local_error) {
          clearPendingLocalLogin();
          setError(data.data.local_error);
          return;
        }
        const userData = {
          ...data.data,
          username: username,
          isSchoolUser: true,
          name: data.data.name,
          class_name: data.data.className,
          student_number: data.data.studentNumber,
          student_id: data.data.studentID,
          studentID: data.data.studentID,
          unified_user_id: data.data.studentID,
          user_type: "campus",
          organizationId: data.data.organizationId || null,
          branding: data.data.branding || null,
        };
        onLogin(userData);
      } else {
        setError(data.message || t('auth_login_failed'));
      }
    } catch (error) {
      console.error("Login error:", error);
      setError(t('auth_login_error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGuestLogin = async (e) => {
    e.preventDefault();
    if (getPendingLocalLogin()) {
      setError("本机实验员暂不支持访客");
      return;
    }
    setError("");
    setIsLoading(true);
    try {
      const apiUrl = `${AUTH_SERVER_URL}/api/login`;

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const data = await response.json();

      if (response.ok) {
        if (data.message === "登录成功") {
          const userData = {
            ...data.user,
            username: data.user.username,
            isGuestUser: true,
            unified_user_id: `guest_${data.user.ID || data.user.username || Math.random().toString(36).substring(2, 10)}`,
            user_type: "guest"
          };
          onLogin(userData);
        } else {
          setError(data.message || "登录失败");
        }
      } else {
        setError(data.message || t('auth_login_failed'));
      }
    } catch (error) {
      console.error("Login error:", error);
      setError(t('auth_login_error'));
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleCasLogin = () => {
    const serviceURL = encodeURIComponent(CAS_CALLBACK_URL);
    localStorage.setItem('cas_auth_source', window.location.href);
    localStorage.setItem('cas_auth_time', new Date().toISOString());
    const casLoginUrl = `${CAS_SERVER_URL}/authserver/login?service=${serviceURL}`;
    window.location.href = casLoginUrl;
  };

  // 🆕 处理教育智联网CAS登录
  const handleEduNetCasLogin = () => {
    const serviceURL = encodeURIComponent(EDUNET_CAS_CALLBACK_URL);
    localStorage.setItem('edunet_cas_auth_source', window.location.href);
    localStorage.setItem('edunet_cas_auth_time', new Date().toISOString());
    const edunetCasLoginUrl = `${EDUNET_CAS_SERVER_URL}/sso/login?service=${serviceURL}`;
    window.location.href = edunetCasLoginUrl;
  };

  // 🆕 处理高教社 HEP Authing SSO 登录
  const handleHepAuthingLogin = async () => {
    const { AuthingSSO } = await import('@authing/sso');
    const hepAuthing = new AuthingSSO({
      appId: HEP_AUTHING_APP_ID,
      redirectUri: HEP_AUTHING_REDIRECT_URI,
      origin: HEP_AUTHING_ORIGIN,
    });
    localStorage.setItem('hep_authing_auth_source', window.location.href);
    localStorage.setItem('hep_authing_auth_time', new Date().toISOString());
    hepAuthing.login({
      scope: 'openid profile email phone',
      responseMode: '',
      responseType: 'code',
      state: Math.random().toString()
    });
  };

  return (
    <div className={styles.authForm}>
      <div className={styles.tabContent}>
        {/* 校园用户为默认且唯一的主入口 */}
        {loginType === 'school' && (
          <form onSubmit={handleSchoolLogin} className={styles.loginForm}>
            {getPendingLocalLogin() ? (
              <div className={styles.localLoginBanner}>
                <span>正在为本机 YouLabber 实验员登录</span>
                <button type="button" className={styles.linkBtn} onClick={() => { clearPendingLocalLogin(); window.location.reload(); }}>取消</button>
              </div>
            ) : null}
            <div className={styles.loginHeading}>
              <h2 className={styles.formTitle}>{t('auth_campus_account')}</h2>
            </div>
            <div className={styles.inputGroup}>
              <label htmlFor="school_username" className={styles.label}>{t('auth_school_username')}</label>
              <input
                id="school_username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                required
                className={`${styles.input} ${error && styles.inputError}`}
              />
            </div>
            <div className={styles.inputGroup}>
              <label htmlFor="school_password" className={styles.label}>{t('auth_password')}</label>
              <input
                id="school_password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                className={`${styles.input} ${error && styles.inputError}`}
              />
            </div>
            <div className={styles.errorMessage} aria-live="polite">{error}</div>
            <button type="submit" className={styles.submitBtn} disabled={isLoading}>
              {isLoading ? <Spinner /> : t('auth_login_btn')}
            </button>
            <p className={styles.helperText}>
              {t('auth_account_hint')}
            </p>
            <div className={styles.alternativeLogin}>
              <div className={styles.alternativeDivider}><span>{t('auth_other_methods')}</span></div>
              <div className={styles.alternativeActions}>
                <button type="button" className={styles.secondaryLoginButton} onClick={handleCasLogin}>
                  <AccountBalanceIcon fontSize="small" />{t('auth_bupt')}
                </button>
                <button type="button" className={styles.secondaryLoginButton} onClick={handleEduNetCasLogin}>
                  <SchoolIcon fontSize="small" />{t('auth_edunet')}
                </button>
                <button type="button" className={styles.secondaryLoginButton} onClick={handleHepAuthingLogin}>
                  <SchoolIcon fontSize="small" />{t('auth_hep')}
                </button>
              </div>
              <button type="button" className={styles.guestEntry} onClick={() => setLoginType('guest')}>
                {FEATURE_FLAGS.GUEST_LOGIN_ENABLED ? t('auth_guest_login') : t('auth_guest_apply')} →
              </button>
            </div>
          </form>
        )}

        {/* 访客用户登录视图 / 商务合作视图 */}
        {loginType === 'guest' && (
          FEATURE_FLAGS.GUEST_LOGIN_ENABLED ? (
            // 访客登录表单（功能开启时显示）
            <form onSubmit={handleGuestLogin} className={styles.loginForm}>
              {getPendingLocalLogin() ? (
                <p className={styles.localLoginNote}>本机实验员暂不支持访客，请返回校园账号登录。</p>
              ) : null}
              <button type="button" className={styles.backToCampus} onClick={() => setLoginType('school')}>
                ← {t('auth_campus_account')}
              </button>
              <div className={styles.loginHeading}>
                <span className={styles.eyebrow}>{t('auth_guest')}</span>
                <h2 className={styles.formTitle}>{t('auth_guest_login')}</h2>
              </div>
              <div className={styles.inputGroup}>
                <label htmlFor="guest_username" className={styles.label}>{t('auth_guest_username')}</label>
                <input
                  id="guest_username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  required
                  className={`${styles.input} ${error && styles.inputError}`}
                />
              </div>
              <div className={styles.inputGroup}>
                <label htmlFor="guest_password" className={styles.label}>{t('auth_password')}</label>
                <input
                  id="guest_password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  className={`${styles.input} ${error && styles.inputError}`}
                />
              </div>
              <div className={styles.errorMessage} aria-live="polite">{error}</div>
              <button type="submit" className={styles.submitBtn} disabled={isLoading}>
                {isLoading ? <Spinner /> : t('auth_login_btn')}
              </button>
              <div className={styles.guestActions}>
                <span>{t('auth_no_account')}</span>
                <button
                  type="button"
                  onClick={onSwitchToRegister}
                  className={styles.linkBtn}
                >
                  {t('auth_register_now')}
                </button>
              </div>
            </form>
          ) : (
            // 商务合作信息（功能关闭时显示）
            <div className={styles.businessContactView}>
              <button type="button" className={styles.backToCampus} onClick={() => setLoginType('school')}>
                ← {t('auth_campus_account')}
              </button>
              <div className={styles.businessIcon}>
                <BusinessIcon style={{ fontSize: 40, color: '#176b91' }} />
              </div>
              <h3 className={styles.businessTitle}>{t('auth_maintenance_title')}</h3>
              <p className={styles.businessDesc} dangerouslySetInnerHTML={{ __html: t('auth_maintenance_desc') }} />
              <div className={styles.businessEmail}>
                <EmailIcon style={{ fontSize: 18, marginRight: 8, color: '#176b91' }} />
                <a href={`mailto:${FEATURE_FLAGS.BUSINESS_CONTACT_EMAIL}`} className={styles.emailLink}>
                  {FEATURE_FLAGS.BUSINESS_CONTACT_EMAIL}
                </a>
              </div>
              <p className={styles.businessNote}>
                {t('auth_maintenance_note')}
              </p>
            </div>
          )
        )}
      </div>
      
      <div className={styles.footerLinks}>
        <a
          href={`${GVA_FRONTEND_URL}/#/login`}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.teacherLoginLink}
        >
          <SupervisorAccountIcon fontSize="small" /> {t('auth_teacher_login')} &gt;
        </a>
      </div>

      <LegalBar />
    </div>
  );
}

// registerFieldLabels 已移除，改用 t() 直接翻译

function RegisterForm({ onRegister, onSwitchToLogin }) {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        username: "",
        password: "",
        email: "",
        verify_code: "",
        invitation_code: "",
        name: "",
        class_number: "",
        faculty: "",
        school: "",
    });
    const [countdown, setCountdown] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [showVerifyCode, setShowVerifyCode] = useState(false); // 控制验证码框显示

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const sendVerifyCode = async () => {
        if (!formData.email) {
            alert(t('auth_email_required'));
            return;
        }
        
        // 简单邮箱格式验证
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            alert(t('auth_email_invalid'));
            return;
        }
        
        try {
            const response = await fetch(`${REMOTE_SMS_SERVER_URL}/api/send_email_code`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: formData.email }),
            });
            const data = await response.json();
            if (response.ok || data.message === "验证码已发送") {
                alert(t('auth_code_sent'));
                setCountdown(60);
                setShowVerifyCode(true); // 显示验证码输入框
            } else {
                alert(data.error || t('auth_code_send_failed'));
            }
        } catch (err) {
            alert(t('auth_code_send_error'));
        }
    };
    
    useEffect(() => {
        if (countdown > 0) {
            const timer = setInterval(() => {
                setCountdown((prev) => prev - 1);
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [countdown]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // 验证邮箱验证码是否已发送和填写
        if (!formData.email || !showVerifyCode || !formData.verify_code) {
            setError(t('auth_code_required'));
            return;
        }
        
        setIsLoading(true);
        setError("");
        try {
            const response = await fetch(`${AUTH_SERVER_URL}/api/register`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });
            const data = await response.json();
            if (response.ok) {
                alert(t('auth_register_success'));
                onRegister();
            } else {
                setError(data.error || t('auth_register_failed'));
            }
        } catch (err) {
            setError(t('auth_register_error'));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className={styles.authForm}>
            <div className={styles.registerHeader}>
                <button type="button" onClick={onSwitchToLogin} className={styles.backBtn}>
                    &lt; {t('auth_back_login')}
                </button>
                <h2 className={styles.formTitle}>{t('auth_register')}</h2>
            </div>

            <fieldset className={styles.fieldset}>
                <legend className={styles.legend}>{t('auth_account_info')}</legend>
                <div className={styles.formGrid}>
                    <div className={`${styles.inputGroup} ${styles.fullWidth}`}>
                        <input id="username" name="username" type="text" value={formData.username} onChange={handleChange} placeholder=" " required className={styles.input} />
                        <label htmlFor="username" className={styles.label}>{t('auth_username')}</label>
                    </div>
                    <div className={`${styles.inputGroup} ${styles.fullWidth}`}>
                        <input id="password" name="password" type="password" value={formData.password} onChange={handleChange} placeholder=" " required className={styles.input} />
                        <label htmlFor="password" className={styles.label}>{t('auth_password')}</label>
                    </div>
                </div>
            </fieldset>

            <fieldset className={styles.fieldset}>
                <legend className={styles.legend}>{t('auth_personal_info')}</legend>
                <div className={styles.formGrid}>
                    <div className={styles.inputGroup}>
                        <input id="name" name="name" type="text" value={formData.name} onChange={handleChange} placeholder=" " required className={styles.input} />
                        <label htmlFor="name" className={styles.label}>{t('auth_name')}</label>
                    </div>
                    <div className={styles.inputGroup}>
                        <input id="school" name="school" type="text" value={formData.school} onChange={handleChange} placeholder=" " required className={styles.input} />
                        <label htmlFor="school" className={styles.label}>{t('auth_school')}</label>
                    </div>
                    <div className={styles.inputGroup}>
                        <input id="faculty" name="faculty" type="text" value={formData.faculty} onChange={handleChange} placeholder=" " required className={styles.input} />
                        <label htmlFor="faculty" className={styles.label}>{t('auth_faculty')}</label>
                    </div>
                    <div className={styles.inputGroup}>
                        <input id="class_number" name="class_number" type="text" value={formData.class_number} onChange={handleChange} placeholder=" " required className={styles.input} />
                        <label htmlFor="class_number" className={styles.label}>{t('auth_class_number')}</label>
                    </div>
                </div>
            </fieldset>

            <fieldset className={styles.fieldset}>
                <legend className={styles.legend}>{t('auth_security')}</legend>
                <div className={styles.formGrid}>
                    <div className={`${styles.inputGroup} ${styles.fullWidth}`}>
                        <div className={styles.emailInputContainer}>
                            <input 
                                id="email" 
                                name="email" 
                                type="email" 
                                value={formData.email} 
                                onChange={handleChange} 
                                placeholder={t('auth_email_placeholder')} 
                                required 
                                className={`${styles.input} ${styles.emailInput}`} 
                            />
                            <label htmlFor="email" className={styles.label}>{t('auth_email')}</label>
                            <button type="button" onClick={sendVerifyCode} disabled={countdown > 0 || !formData.email} className={styles.emailVerifyBtn}>
                                {countdown > 0 ? t('auth_resend_code', { count: countdown }) : t('auth_send_code')}
                            </button>
                        </div>
                    </div>
                    
                    {showVerifyCode && (
                        <div className={`${styles.inputGroup} ${styles.fullWidth}`}>
                            <input 
                                id="verify_code" 
                                name="verify_code" 
                                type="text" 
                                value={formData.verify_code} 
                                onChange={handleChange} 
                                placeholder={t('auth_verify_code_placeholder')} 
                                maxLength="6"
                                required 
                                className={styles.input} 
                            />
                            <label htmlFor="verify_code" className={styles.label}>{t('auth_verify_code_label')}</label>
                            <div className={styles.verifyCodeHint}>
                                {t('auth_verify_code_hint')}
                            </div>
                        </div>
                    )}
                    
                     <div className={`${styles.inputGroup} ${styles.fullWidth}`}>
                        <input id="invitation_code" name="invitation_code" type="text" value={formData.invitation_code} onChange={handleChange} placeholder={t('auth_invitation_placeholder')} required={false} className={styles.input} />
                        <label htmlFor="invitation_code" className={styles.label}>{t('auth_invitation_placeholder')}</label>
                    </div>
                </div>
            </fieldset>

            <div className={styles.errorMessage}>{error}</div>
            <button type="submit" className={`${styles.submitBtn} ${styles.fullWidth}`} disabled={isLoading}>
                 {isLoading ? <Spinner /> : t('auth_register_btn')}
            </button>
        </form>
    );
}


export default function AuthPage({ onLogin, onRegisterSuccess }) {
  const { t, i18n } = useTranslation();
  const [showRegister, setShowRegister] = useState(false);
  const [showDeviceWarning, setShowDeviceWarning] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [, forceUpdate] = useState(0); // 用于语言切换后刷新显示

  // 🔍 设备检测函数 - 增强版（包含微信等内置浏览器检测）
  const detectDevice = () => {
    const ua = navigator.userAgent;
    
    // 检测移动端操作系统
    const isMobileOS = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    
    // 检测常见移动端浏览器（包括微信、QQ等内置浏览器）
    const isMobileBrowser = /MicroMessenger|QQ\/|Mobile|mobile|WeChat|MQBHD|UCBrowser/i.test(ua);
    
    // 检测平板（排除iPad的移动端判断）
    const isTablet = /iPad|Android(?!.*Mobile)/i.test(ua);
    
    // 综合判断：移动端OS或移动端浏览器都算作移动设备
    const isMobile = (isMobileOS || isMobileBrowser) && !isTablet;
    
    console.log('设备检测:', { ua, isMobileOS, isMobileBrowser, isTablet, isMobile });
    
    return {
      isMobile: isMobile,
      isTablet: isTablet,
      isPCBrowser: !isMobile && !isTablet
    };
  };

  // 🚨 组件挂载时检测设备
  useEffect(() => {
    const device = detectDevice();
    if (device.isMobile || device.isTablet) {
      setShowDeviceWarning(true);
    }
  }, []);

  return (
    <div className={styles.authContainer}>
      {/* 🌐 语言切换按钮 - 右上角 */}
      <div className={styles.langSwitcher}>
        <button
          className={styles.langButton}
          onClick={() => setShowLangMenu(!showLangMenu)}
          onBlur={() => setTimeout(() => setShowLangMenu(false), 150)}
        >
          🌐 {getCurrentLanguageLabel()}
          <span className={styles.langArrow}>{showLangMenu ? '▲' : '▼'}</span>
        </button>
        {showLangMenu && (
          <div className={styles.langDropdown}>
            {SUPPORTED_LANGUAGES.map((lang) => {
              const currentLang = getPreferredLanguage();
              const isSelected = currentLang === lang.code;
              return (
                <div
                  key={lang.code}
                  className={`${styles.langOption} ${isSelected ? styles.langOptionActive : ''}`}
                  onMouseDown={() => {
                    switchLanguage(lang.code, i18n);
                    setShowLangMenu(false);
                    forceUpdate(n => n + 1);
                  }}
                >
                  <span className={styles.langCheck}>{isSelected ? '✓' : ''}</span>
                  {lang.label}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 🚨 移动端警告对话框 */}
      {showDeviceWarning && (
        <div className={styles.warningOverlay}>
          <div className={styles.mobileBrandIdentity} aria-hidden="true">
            <img src="/avatars/mozi.png" alt="" className={styles.mobileBrandLogo} />
            <p>{t('auth_platform_title')}</p>
            <span>{t('auth_platform_description')}</span>
          </div>
          <div className={styles.warningDialog}>
            <div className={styles.warningIcon}>⚠️</div>
            <h2 className={styles.warningTitle}>{t('auth_device_warning_title')}</h2>
            <div className={styles.warningContent}>
              <p className={styles.warningText}>
                {t('auth_device_warning_pc')}
              </p>
              <p className={styles.warningText}>
                {t('auth_device_warning_browsers')}
              </p>
              <ul className={styles.browserList}>
                <li>Chrome 76+</li>
                <li>Edge 79+</li>
                <li>Safari 9+</li>
                <li>Firefox 103+</li>
              </ul>
              <p className={styles.warningText}>
                {t('auth_device_warning_zoom')}
              </p>
              <p className={styles.warningHighlight}>
                {t('auth_device_warning_mobile')}
              </p>
            </div>
            <button 
              className={styles.warningButton}
              onClick={() => setShowDeviceWarning(false)}
            >
              {t('auth_device_warning_ok')}
            </button>
          </div>
        </div>
      )}

      <main className={styles.authLayout}>
        <section className={styles.brandPanel} aria-label={t('auth_platform_title')}>
          <div className={styles.brandMark}>
            <img src="/avatars/mozi.png" alt="邮百工" className={styles.logo} />
          </div>
          <div className={styles.brandCopy}>
            <p className={styles.brandKicker}>{t('auth_platform_kicker')}</p>
            <div className={styles.brandAccent} aria-hidden="true" />
            <h1 className={styles.brandTitle}>
              <span>{t('auth_platform_title_line1')}</span>
              <span>{t('auth_platform_title_line2')}</span>
            </h1>
            <p className={styles.brandDescription}>
              <span>{t('auth_platform_description_line1')}</span>
              <span>{t('auth_platform_description_line2')}</span>
            </p>
          </div>
          <LoginBroadcast />
        </section>

        <section className={styles.loginPanel} aria-label={t('auth_login')}>
          <div className={styles.authForms}>
            {!showRegister ? (
              <LoginForm 
                onLogin={onLogin} 
                onSwitchToRegister={() => setShowRegister(true)}
              />
            ) : (
              <RegisterForm 
                onRegister={() => {
                    setShowRegister(false);
                    onRegisterSuccess();
                }} 
                onSwitchToLogin={() => setShowRegister(false)}
              />
            )}
            
            <div className={styles.browserCompatibility}>
              <p className={styles.compatibilityText}>
                {t('auth_browser_compatibility')}
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}